import React, { useState, useEffect, useCallback } from 'react';
import { View, ScrollView, Switch, Alert, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/Card';
import { Body, Caption, SubHeading } from '@/components/ui/Text';
import { OfflineSectionCard } from './OfflineSectionCard';
import { StorageUsageCard } from './StorageUsageCard';
import { useUserStore } from '@/state/userStore';
import { subscribeToUserTrips, type Trip } from '@/services/trips';
import { networkMonitor } from '@/services/networkMonitor';
import {
  getDownloadedRegions,
  formatFileSize as formatMapSize,
  deleteAllOfflineMaps,
} from '@/services/offlineMaps';
import {
  getOfflineDocumentsSize,
  formatFileSize as formatDocSize,
  clearAllOfflineDocuments,
  isDocumentOffline,
  downloadAllPendingFileDocumentsForOffline,
} from '@/services/offlineDocuments';
import { getSafetyOfflineUiState, clearSafetyCache } from '@/services/offlineSafety';
import { getLastSyncTime, clearAllCachedTrips, getCachedTrips, refreshOfflineReceiptCopiesForTrips } from '@/services/offlineTrips';
import { useRouter } from 'expo-router';
import {
  getAutoDownloadDocumentsEnabled,
  setAutoDownloadDocumentsEnabled,
} from '@/services/autoDownloadPreference';
import { listDocuments, type Document } from '@/services/documentVault';

type OfflineSettingsScreenProps = {
  /** Parent supplies `Screen` chrome (e.g. modal). Avoids duplicate titles and sync banners. */
  embedded?: boolean;
};

export function OfflineSettingsScreen({ embedded = false }: OfflineSettingsScreenProps) {
  const theme = useTheme();
  const router = useRouter();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [autoDownloadEnabled, setAutoDownloadEnabled] = useState(true);
  const [storageBreakdown, setStorageBreakdown] = useState({
    maps: 0,
    documents: 0,
    itinerary: 0,
    safety: 0,
  });
  const [lastSyncTimes, setLastSyncTimes] = useState({
    maps: null as number | null,
    documents: null as number | null,
    itinerary: null as number | null,
    safety: null as number | null,
  });
  const [isDownloaded, setIsDownloaded] = useState({
    maps: false,
    documents: false,
    itinerary: false,
    safety: false,
  });
  const [mapsRegionCount, setMapsRegionCount] = useState(0);
  const [inventoryBump, setInventoryBump] = useState(0);
  const [downloadingVaultDocs, setDownloadingVaultDocs] = useState(false);

  // Load auto-download setting (synced with Documents tab / tripvio + legacy keys)
  useEffect(() => {
    void getAutoDownloadDocumentsEnabled()
      .then(setAutoDownloadEnabled)
      .catch((error) => console.error('[OfflineSettings] Error loading auto-download setting:', error));
  }, []);

  // Subscribe to trips
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setTrips([]);
      return;
    }

    const unsubscribe = subscribeToUserTrips(firebaseUser.uid, (userTrips) => {
      setTrips((prev) => {
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          userTrips.length === 0
        ) {
          return prev;
        }
        return userTrips;
      });
    }, {
      onError: (error) => {
        if (__DEV__) {
          console.warn('[OfflineSettings] subscribeToUserTrips error:', error);
        }
      },
    });

    return () => unsubscribe();
  }, [firebaseUser?.uid]);

  // Update storage breakdown and status
  useEffect(() => {
    const updateStorageInfo = async () => {
      try {
        // Maps
        let mapsSize = 0;
        let mapsDownloaded = false;
        if (Platform.OS !== 'web') {
          try {
            const regions = await getDownloadedRegions();
            mapsDownloaded = regions.length > 0;
            mapsSize = regions.reduce((sum, r) => sum + (r.size || 0), 0);
            setMapsRegionCount(regions.length);
          } catch (error) {
            console.error('[OfflineSettings] Error getting maps info:', error);
          }
        }

        // Documents
        let documentsSize = 0;
        let documentsDownloaded = false;
        try {
          documentsSize = await getOfflineDocumentsSize();
          documentsDownloaded = documentsSize > 0;
        } catch (error) {
          console.error('[OfflineSettings] Error getting documents info:', error);
        }

        // Itinerary: only count trips with a non-expired local pack
        let itinerarySize = 0;
        let itineraryDownloaded = false;
        let cachedTripCount = 0;
        try {
          const cachedTrips = await getCachedTrips(firebaseUser?.uid ?? null);
          cachedTripCount = cachedTrips.length;
          itineraryDownloaded = cachedTripCount > 0;
          itinerarySize = cachedTripCount * 10 * 1024;
        } catch (error) {
          console.error('[OfflineSettings] Error getting itinerary info:', error);
        }

        // Safety (explicit offline pack only; matches Safety tab / remove behavior)
        let safetySize = 0;
        let safetyDownloaded = false;
        let safetyLastSyncForRow: number | null = null;
        try {
          const safetyUi = await getSafetyOfflineUiState();
          safetyDownloaded = safetyUi.showAsDownloaded;
          safetyLastSyncForRow = safetyUi.lastSynced;
          safetySize = safetyUi.showAsDownloaded ? trips.length * 5 * 1024 : 0;
        } catch (error) {
          console.error('[OfflineSettings] Error getting safety info:', error);
        }

        setStorageBreakdown({
          maps: mapsSize,
          documents: documentsSize,
          itinerary: itinerarySize,
          safety: safetySize,
        });

        setIsDownloaded({
          maps: mapsDownloaded,
          documents: documentsDownloaded,
          itinerary: itineraryDownloaded,
          safety: safetyDownloaded,
        });

        // Get last sync times
        const itineraryLastSync = await getLastSyncTime();

        setLastSyncTimes({
          maps: mapsDownloaded ? Date.now() : null, // Approximate
          documents: documentsDownloaded ? Date.now() : null, // Approximate
          itinerary: itineraryLastSync,
          safety: safetyLastSyncForRow,
        });
      } catch (error) {
        console.error('[OfflineSettings] Error updating storage info:', error);
      }
    };

    updateStorageInfo();
    const interval = setInterval(updateStorageInfo, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [trips.length, firebaseUser?.uid, inventoryBump]);

  const bumpInventory = useCallback(() => setInventoryBump((n) => n + 1), []);

  const handleDownloadVaultDocuments = useCallback(() => {
    void (async () => {
      if (!networkMonitor.isOnline()) {
        Alert.alert('Offline', 'Connect to the internet to download documents to this device.');
        return;
      }
      if (!firebaseUser?.uid) {
        Alert.alert('Sign in required', 'Sign in to download your documents.');
        return;
      }

      let allDocs: Document[] = [];
      try {
        allDocs = await listDocuments();
      } catch (e) {
        console.error('[OfflineSettings] listDocuments', e);
        Alert.alert('Could not load documents', 'Open the Documents tab once while online, then try again.');
        return;
      }

      let pending = 0;
      for (const d of allDocs) {
        if (d.entryType === 'manual') continue;
        if (await isDocumentOffline(d.id)) continue;
        pending++;
      }

      if (pending === 0) {
        Alert.alert(
          'Up to date',
          'Every file-backed document is already saved on this device for offline viewing.',
        );
        return;
      }

      const confirmed = await new Promise<boolean>((resolve) => {
        Alert.alert(
          'Download documents',
          `Download ${pending} document${pending !== 1 ? 's' : ''} for offline access? This may use noticeable storage space.`,
          [
            { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
            { text: 'Download', onPress: () => resolve(true) },
          ],
        );
      });

      if (!confirmed) return;

      setDownloadingVaultDocs(true);
      try {
        const { success, failed, downloadedIds } = await downloadAllPendingFileDocumentsForOffline(allDocs);
        const downloadedSet = new Set(downloadedIds);
        const touchedTripIds = new Set<string>();
        for (const document of allDocs) {
          if (!downloadedSet.has(document.id)) continue;
          for (const tid of document.tripIds ?? []) {
            if (typeof tid === 'string' && tid.length > 0) touchedTripIds.add(tid);
          }
        }

        if (touchedTripIds.size > 0) {
          await refreshOfflineReceiptCopiesForTrips(touchedTripIds, (tripId) => trips.find((t) => t.id === tripId));
        }

        bumpInventory();
        Alert.alert(
          'Download complete',
          `Saved ${success} document${success !== 1 ? 's' : ''} on this device.${failed > 0 ? ` ${failed} could not be downloaded.` : ''}`,
        );
      } catch (e) {
        console.error('[OfflineSettings] bulk document download', e);
        Alert.alert('Error', 'Some documents could not be downloaded. Try again from the Documents tab.');
      } finally {
        setDownloadingVaultDocs(false);
      }
    })();
  }, [bumpInventory, firebaseUser?.uid, trips]);

  const handleToggleAutoDownload = async (value: boolean) => {
    try {
      await setAutoDownloadDocumentsEnabled(value);
      setAutoDownloadEnabled(value);
    } catch (error) {
      console.error('[OfflineSettings] Error saving auto-download setting:', error);
      Alert.alert('Error', 'Failed to update auto-download setting.');
    }
  };

  const handleOpenMapsForOffline = () => {
    if (Platform.OS === 'web') {
      Alert.alert('Offline maps are only available on mobile devices.');
      return;
    }

    const tripWithDestination = trips.find((t) => t.destination);
    if (!tripWithDestination) {
      Alert.alert('Add a destination', 'Pick a trip with a destination to download maps.');
      return;
    }

    router.push(`/(tabs)/maps?tripId=${tripWithDestination.id}&openOffline=1`);
  };

  const confirmRemoveMaps = () => {
    Alert.alert(
      'Remove offline maps?',
      'This deletes downloaded map regions from this device. You can download them again later.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            void (async () => {
              try {
                await deleteAllOfflineMaps();
                bumpInventory();
              } catch (e) {
                console.error('[OfflineSettings] remove maps', e);
                Alert.alert('Error', 'Could not remove all offline maps.');
              }
            })();
          },
        },
      ],
    );
  };

  const confirmRemoveDocuments = () => {
    Alert.alert(
      'Remove offline documents?',
      'Files saved for offline viewing will be deleted from this device. Your cloud copies are unchanged.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            void (async () => {
              try {
                await clearAllOfflineDocuments();
                bumpInventory();
              } catch (e) {
                console.error('[OfflineSettings] remove documents', e);
                Alert.alert('Error', 'Could not remove offline documents.');
              }
            })();
          },
        },
      ],
    );
  };

  const confirmRemoveItinerary = () => {
    Alert.alert(
      'Remove cached trip packs?',
      'Offline copies of trip plans, bookings, and itinerary on this device will be cleared. Nothing is deleted from the cloud.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            void (async () => {
              try {
                await clearAllCachedTrips();
                bumpInventory();
              } catch (e) {
                console.error('[OfflineSettings] remove itinerary cache', e);
                Alert.alert('Error', 'Could not remove cached trip data.');
              }
            })();
          },
        },
      ],
    );
  };

  const confirmRemoveSafety = () => {
    Alert.alert(
      'Remove offline safety info?',
      'Cached safety and advisory data on this device will be cleared.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            void (async () => {
              try {
                await clearSafetyCache();
                bumpInventory();
              } catch (e) {
                console.error('[OfflineSettings] remove safety', e);
                Alert.alert('Error', 'Could not remove safety cache.');
              }
            })();
          },
        },
      ],
    );
  };

  const totalStorage = storageBreakdown.maps + storageBreakdown.documents + 
                       storageBreakdown.itinerary + storageBreakdown.safety;

  const scrollContent = (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: theme.spacing(3), paddingBottom: theme.spacing(2) }}>
        {/* Auto-Download Toggle */}
        <Card variant="outlined">
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
              <SubHeading style={{ fontSize: 18 }}>Auto-Download</SubHeading>
              <Caption color={theme.colors.textSecondary}>
                Automatically download travel documents for trips within 7 days.
              </Caption>
              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                Existing offline copies stay on this device. Turn this off to stop new automatic document downloads
                only; use Remove on each section below to free space.
              </Caption>
            </View>
            <Switch
              value={autoDownloadEnabled}
              onValueChange={handleToggleAutoDownload}
              trackColor={{ false: theme.colors.borderMuted, true: theme.colors.primary + '80' }}
              thumbColor={autoDownloadEnabled ? theme.colors.primary : theme.colors.textSecondary}
            />
          </View>
        </Card>

        {/* Storage Usage */}
        <StorageUsageCard
          totalSize={totalStorage}
          breakdown={storageBreakdown}
        />

        {/* Section Cards */}
        <View style={{ gap: theme.spacing(2) }}>
          <SubHeading style={{ fontSize: 20 }}>Offline Content</SubHeading>

          {/* Maps */}
          {Platform.OS !== 'web' && (
            <OfflineSectionCard
              title="Maps"
              icon="map-outline"
              isDownloaded={isDownloaded.maps}
              storageSize={storageBreakdown.maps}
              lastSynced={lastSyncTimes.maps}
              formatStorageSize={formatMapSize}
              description="Download maps for trip destinations"
              onDownload={handleOpenMapsForOffline}
              onDelete={isDownloaded.maps ? confirmRemoveMaps : undefined}
            />
          )}
          {Platform.OS !== 'web' && (
            <Caption color={theme.colors.textSecondary}>
              {mapsRegionCount > 0
                ? `${mapsRegionCount} offline map${mapsRegionCount > 1 ? 's' : ''} · ${formatMapSize(storageBreakdown.maps)}`
                : 'No offline maps downloaded yet'}
            </Caption>
          )}

          {/* Documents */}
          <OfflineSectionCard
            title="Documents"
            icon="document-outline"
            isDownloaded={isDownloaded.documents}
            isDownloading={downloadingVaultDocs}
            storageSize={storageBreakdown.documents}
            lastSynced={lastSyncTimes.documents}
            formatStorageSize={formatDocSize}
            description="Passports, visas, boarding passes, and more"
            onDownload={handleDownloadVaultDocuments}
            onDelete={isDownloaded.documents ? confirmRemoveDocuments : undefined}
          />

          {/* Itinerary */}
          <OfflineSectionCard
            title="Itinerary"
            icon="calendar-outline"
            isDownloaded={isDownloaded.itinerary}
            storageSize={storageBreakdown.itinerary}
            lastSynced={lastSyncTimes.itinerary}
            description="Trip plans, bookings, and activities"
            onDownload={() => {
              Alert.alert('Download Itinerary', 'Go to the Planning tab to download itinerary data for offline access.');
            }}
            onDelete={isDownloaded.itinerary ? confirmRemoveItinerary : undefined}
          />

          {/* Safety */}
          <OfflineSectionCard
            title="Safety"
            icon="shield-outline"
            isDownloaded={isDownloaded.safety}
            storageSize={storageBreakdown.safety}
            lastSynced={lastSyncTimes.safety}
            description="Emergency contacts, advisories, and embassy info"
            onDownload={() => {
              Alert.alert('Download Safety Info', 'Go to the Safety tab to download safety information for offline access.');
            }}
            onDelete={isDownloaded.safety ? confirmRemoveSafety : undefined}
          />
        </View>

        {/* Info Card */}
        <Card variant="outlined" style={{ backgroundColor: theme.colors.primary + '10' }}>
          <View style={{ flexDirection: 'row', gap: theme.spacing(2) }}>
            <Ionicons name="information-circle-outline" size={24} color={theme.colors.primary} />
            <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
              <Body style={{ fontWeight: '600' }}>About Offline Mode</Body>
              <Caption color={theme.colors.textSecondary}>
                Offline content is stored locally on your device. Make sure you have enough storage space available. 
                Content is automatically synced when you're online.
              </Caption>
            </View>
          </View>
        </Card>
    </ScrollView>
  );

  if (embedded) {
    return <View style={{ flex: 1 }}>{scrollContent}</View>;
  }

  return (
    <Screen title="Offline Mode" subtitle="Manage offline content and auto-download settings">
      {scrollContent}
    </Screen>
  );
}






